/**
 * useThoughtForToday
 *
 * The curated thought for the student's local date, from the server's
 * catalog. Resolves to `null` when there is none to show (empty data mode, or
 * a server whose catalog has not been seeded) - callers leave the section
 * out rather than showing an error on a page meant to be calm.
 *
 * Backend endpoint: GET /wellbeing/thought?date=YYYY-MM-DD
 */
import { useQuery } from '@tanstack/react-query';
import { wellbeingApi, type WellbeingThought } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { loadMockWellbeingThoughts } from '@/data';
import { localDateKey, rotationIndex } from '@/lib/wellbeing/thoughtDate';

export function useThoughtForToday(now: number, { enabled = true } = {}) {
  const { isMock, isEmpty } = useDataMode();
  const date = localDateKey(now);

  return useQuery<WellbeingThought | null>({
    queryKey: [...queryKeys.wellbeing.thought(date), isMock, isEmpty],
    enabled,
    queryFn: async () => {
      if (isEmpty) return null;
      if (isMock) {
        const { mockWellbeingThoughts: all } =
          await loadMockWellbeingThoughts();
        return all[rotationIndex(date, all.length)];
      }
      return wellbeingApi.getThought(date);
    },
    // The same all day: the key changes with the date, so nothing goes stale.
    staleTime: Infinity,
    retry: 1,
  });
}
